import React from 'react'
import '@ant-design/compatible/assets/index.css'
import { ConfigurationContext } from './configuration-context'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { RuleBuilderV2 } from '../../rule-builder/rule-builder'
import { getClassNames } from '../../../_utils/classnames'
import { ComboSegmentSelection, Well } from '@pushly/aqe/lib/components'
import { ComboSegmentSelectionType } from '@pushly/aqe/lib/components/combo-segment-selection/combo-segment-selection'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Popover, Radio, Select, Skeleton, Switch } from 'antd'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { JOURNEY_CRITERIA_FIELDS } from './constants'
import { SegmentDto } from '../../../dtos/segment'
import { JourneyAudienceEvaluationFrequency } from '../../../enums/journey-audience-evaluation-frequency'
import { RadioChangeEvent } from 'antd/lib/radio'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { DomainService } from '../../../services'
import { DictionaryService } from '../../../services/dictionary'
import { buildLanguageCustomField } from '../../segment-builder/custom-fields/build-language.custom-field'
import '../style/audience-well.scss'
import { generateUID } from '../../../_utils/uid'
import SegmentBuilder from '../../segment-builder/segment-builder'
import { SegmentSource } from '@pushly/models/lib/enums'
import { Drawer } from '../../drawer/drawer'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { getAudienceSegmentOptionGroups } from 'src/_utils/segment-list'
import { JourneyType, TriggerType } from '@pushly/models/lib/enums/journeys'
import { EcommItemGroup } from '../../../dtos/domain'
import type { ITriggerStepConfiguration } from '@pushly/models/lib/structs/journeys/steps/step-interfaces'
import type { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'

interface IAudienceWell {
    submitConfiguration?: Record<string, unknown>
    getSegments: () => Promise<SegmentDto[]>
}

interface IState {
    initialSegmentsLoader?: Promise<SegmentDto[]>

    criteriaFieldsReady: boolean
    languageCodes: { code: string; name: string }[]
    languageCodesLoaded: boolean
    itemGroupsReady: boolean
    itemGroupsLoaded: boolean
    itemGroups: EcommItemGroup[]

    optionsLoaded: boolean
    options: SegmentDto[]

    segmentSearchTerm?: string

    showSegmentDrawer: boolean
    drawerId?: string
    newSegmentType?: string
    newSegmentName?: string
}

export class AudienceWell extends React.Component<IAudienceWell, IState> {
    public static contextType = ConfigurationContext
    public context!: React.ContextType<typeof ConfigurationContext>

    public state: IState = {
        criteriaFieldsReady: false,
        languageCodes: [],
        languageCodesLoaded: false,
        itemGroupsReady: false,
        itemGroupsLoaded: false,
        itemGroups: [],
        showSegmentDrawer: false,
        optionsLoaded: false,
        options: [],
    }

    private readonly appState: AppState
    private readonly domainSvc: DomainService
    private readonly dictionarySvc: DictionaryService
    private segmentBuilderRef: SegmentBuilder | null = null

    public constructor(props: IAudienceWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.domainSvc = Container.get(DomainService)
        this.dictionarySvc = Container.get(DictionaryService)
    }

    public async componentDidMount() {
        await this.loadSegmentOptions()
        await this.loadCriteriaFields()
        await this.loadItemGroupField()

        const domainActiveChannels = getEnabledDeliveryChannels(this.context.domain, true)
        const domainHasMultipleChannels = domainActiveChannels.length > 1
        // we do not expose the delivery channel selector
        // if the domain does not have multiple channels enabled,
        // so set channels to the active channel
        if (!domainHasMultipleChannels) {
            this.handleChannelSelectionChange(domainActiveChannels)
        }

        // initialize the requested segment loader
        this.setState({
            initialSegmentsLoader: this.loadInitiallySelectedSegments(),
        })
    }

    public render() {
        const { mode, loading, domain, journey, editableState, domainDefaultSegment, flags } = this.context
        const itemGroupIds = journey.configuration.item_group_targeting?.item_group_ids || []
        const domainHasItemGroups = this.state.itemGroups.length > 0

        const isTriggeredJourney = journey.type === JourneyType.TRIGGER
        const trigger = extractJourneyTrigger(journey)
        const triggerConfig = trigger?.configuration
        const triggerType = triggerConfig?.type
        const isSubscribedTrigger = triggerType === TriggerType.SUBSCRIBED
        const triggerParams = triggerConfig?.params

        const audience = journey.configuration.audience
        const segmentIds = audience?.segment_ids
        const xSegmentIds = audience?.excluded_segment_ids
        const hasSegments = !!segmentIds || !!xSegmentIds

        const domainActiveChannels = getEnabledDeliveryChannels(domain, true)
        const domainHasMultipleChannels = domainActiveChannels.length > 1

        let audienceTargetType = 'all'
        if (!!domainDefaultSegment && hasSegments && !!segmentIds) {
            if (segmentIds.length === 0 || segmentIds[0] !== domainDefaultSegment.id) {
                audienceTargetType = 'segments'
            }
        }

        const audienceChangesDisabled = editableState === JourneyEditableState.COMPLETED
        const criteriaFields = [...JOURNEY_CRITERIA_FIELDS]
        if (this.state.languageCodesLoaded && this.state.languageCodes.length) {
            criteriaFields.push(buildLanguageCustomField('language', this.state.languageCodes))
        }

        const targetItemsCapableTriggerTypes = [
            TriggerType.ABANDONED_CART,
            TriggerType.ABANDONED_VIEW,
            TriggerType.SAVED_ITEM,
        ]
        const canShowTargetItemsAudienceOptions =
            isTriggeredJourney && targetItemsCapableTriggerTypes.includes(triggerType as TriggerType) && domainHasItemGroups

        return (
            <Well
                className={getClassNames(null, 'journey-audience-well', 'nested', `mode-${mode}`, {
                    ['final-well']: !!this.props.submitConfiguration,
                })}
                title="Audience"
                hideFooter={!this.props.submitConfiguration}
                {...this.props.submitConfiguration}
            >
                <Skeleton
                    loading={loading}
                    active={true}
                    title={false}
                    paragraph={{
                        rows: 1,
                        width: '100%',
                    }}
                >
                    {domainHasMultipleChannels && (
                        <DeliveryChannelSelector
                            loading={loading}
                            wellMode="ghost"
                            wellNested={true}
                            type="multiple"
                            value={journey.channels}
                            onChange={this.handleChannelSelectionChange}
                            visibleChannels={domainActiveChannels}
                        />
                    )}

                    {!isSubscribedTrigger && (
                        <Well mode="ghost" title="Target Audience" hideFooter={true}>
                            <Skeleton
                                loading={!domainDefaultSegment || !this.state.optionsLoaded}
                                active={true}
                                avatar={false}
                                paragraph={false}
                            >
                                <Radio.Group
                                    className="audience-builder-type-radio"
                                    size="small"
                                    buttonStyle="solid"
                                    disabled={audienceChangesDisabled}
                                    value={audienceTargetType}
                                    onChange={(ev) => this.handleAudienceTypeChange(ev.target.value)}
                                >
                                    <Radio.Button value="all">All Subscribers</Radio.Button>
                                    <Radio.Button value="segments">Specific Segments</Radio.Button>
                                </Radio.Group>

                                {!!domainDefaultSegment && (
                                    <ComboSegmentSelection
                                        size="small"
                                        disabled={audienceChangesDisabled}
                                        value={{
                                            allSubscribers: false,
                                            segmentIds: audience?.segment_ids,
                                            excludedSegmentIds: audience?.excluded_segment_ids,
                                        }}
                                        hideIncluded={audienceTargetType !== 'segments'}
                                        hideExcluded={false}
                                        onChange={(v) => {
                                            const workingModel = structuredClone(this.context.journey)
                                            const workingModelAudience = workingModel.configuration.audience
                                            workingModelAudience.segment_ids = v.segmentIds ?? []
                                            workingModelAudience.excluded_segment_ids = v.excludedSegmentIds

                                            if (v.segmentIds && v.segmentIds.length === 1) {
                                                const segmentId = v.segmentIds[0]
                                                const segment = this.state.options.find(
                                                    (s) => s.id.toString() === segmentId.toString(),
                                                )

                                                if (segment?.iconUrl) {
                                                    workingModelAudience.default_icon_url = segment.iconUrl
                                                }
                                            } else {
                                                workingModelAudience.default_icon_url = undefined
                                            }

                                            this.context.setJourney(workingModel)
                                        }}
                                        options={getAudienceSegmentOptionGroups(this.state.options)}
                                        additionalOptions={this.state.initialSegmentsLoader?.then((v) =>
                                            v.map(this.extractOption),
                                        )}
                                        notFoundContent={(_type) => 'No matching segments'}
                                        stickyOptionRenderer={(type) => (
                                            <div key="new" className={getClassNames(null, 'new-segment-option')}>
                                                {this.renderNewSegmentOption(type, false)}
                                            </div>
                                        )}
                                        onSearch={(v) => this.setState({ segmentSearchTerm: v })}
                                    />
                                )}
                            </Skeleton>

                            <Drawer
                                className={getClassNames('combo-segment-select-new-segment-drawer')}
                                title="Create New Segment"
                                open={this.state.showSegmentDrawer}
                                onSubmit={this.handleSegmentCreation}
                                onClose={this.closeDrawer}
                            >
                                <SegmentBuilder
                                    key={this.state.drawerId ?? 'unknown'}
                                    ref={(el) => (this.segmentBuilderRef = el)}
                                    level="domain"
                                    domainId={this.context.domain.id}
                                    mode="drawer"
                                    name={this.state.newSegmentName}
                                    source={SegmentSource.STANDARD}
                                />
                            </Drawer>
                        </Well>
                    )}

                    {isSubscribedTrigger && (
                        <Well
                            mode="ghost"
                            title="Entry Criteria"
                            hideFooter={true}
                            loading={!this.state.criteriaFieldsReady}
                        >
                            <div className="switch-row criteria-switch">
                                <div className="switch-left">
                                    <Switch
                                        size="small"
                                        disabled={audienceChangesDisabled}
                                        checked={flags.criteriaEnabled}
                                        onChange={this.handleCriteriaEnabledChange}
                                    />
                                </div>
                                <div
                                    className="switch-right"
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={!!flags.criteriaEnabled}
                                    onClick={() => {
                                        if (!audienceChangesDisabled) {
                                            this.handleCriteriaEnabledChange(!flags.criteriaEnabled)
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (!audienceChangesDisabled && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault()
                                            this.handleCriteriaEnabledChange(!flags.criteriaEnabled)
                                        }
                                    }}
                                    style={{ cursor: audienceChangesDisabled ? 'not-allowed' : 'pointer' }}
                                >
                                    Restrict entry to this journey based on subscriber attributes.
                                </div>
                            </div>

                            {flags.criteriaEnabled && this.state.criteriaFieldsReady && (
                                <>
                                    <RuleBuilderV2
                                        disabled={audienceChangesDisabled}
                                        value={triggerParams?.criteria || {}}
                                        onChange={this.handleCriteriaChange}
                                        fields={criteriaFields}
                                    />
                                </>
                            )}
                        </Well>
                    )}

                    {canShowTargetItemsAudienceOptions && (
                        <Well
                            mode="ghost"
                            title={
                                <span>
                                    Target Items
                                    <Popover
                                        overlayClassName="eval-freq-info-popover"
                                        content={
                                            <>
                                                <p>
                                                    Setting a journey’s item groups will restrict the items matched by
                                                    the journey to only those that exist within the group. Contact your
                                                    account manager to create new item groups.
                                                </p>
                                            </>
                                        }
                                    >
                                        <QuestionCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                            hideFooter={true}
                            loading={!this.state.itemGroupsReady}
                        >
                            <div className="switch-row criteria-switch">
                                <div className="switch-left">
                                    <Switch
                                        size="small"
                                        disabled={audienceChangesDisabled}
                                        checked={this.context.flags.itemGroupsEnabled}
                                        onChange={this.handleItemGroupsEnabledChange}
                                    />
                                </div>
                                <div className="switch-right">Restrict this Journey to Specific Items</div>
                            </div>

                            {this.context.flags.itemGroupsEnabled && (
                                <Select
                                    className="journey-item-group-select"
                                    size="small"
                                    mode="multiple"
                                    placeholder="Select at least one Item Group to target"
                                    value={[...itemGroupIds]}
                                    onChange={this.handleItemGroupSelectionChange}
                                >
                                    {this.state.itemGroups.map((group) => (
                                        <Select.Option key={group.id} value={group.id}>
                                            {group.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Well>
                    )}

                    <Well
                        mode="ghost"
                        title={
                            <span>
                                Evaluation Frequency
                                <Popover
                                    overlayClassName="eval-freq-info-popover"
                                    content={
                                        <>
                                            <p>
                                                <b>Journey & Step Entry:</b> The subscriber will be checked against the
                                                audience on journey entry and before entry in to each step. If the
                                                subscriber is no longer part of the audience they will be removed from
                                                the journey.
                                            </p>
                                            <p>
                                                <b>Journey Entry:</b> The subscriber will only be checked against the
                                                audience when they enter the journey. They will continue executing
                                                steps of the journey even if they lapse out of the audience.
                                            </p>
                                        </>
                                    }
                                >
                                    <QuestionCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        }
                        hideFooter={true}
                    >
                        <Radio.Group
                            className="journey-audience-eval-selection"
                            size="small"
                            buttonStyle="solid"
                            disabled={audienceChangesDisabled}
                            defaultValue={JourneyAudienceEvaluationFrequency.STEP_ENTRY}
                            value={audience?.evaluation_frequency ?? JourneyAudienceEvaluationFrequency.STEP_ENTRY}
                            onChange={this.handleEvaluationFreqChange}
                        >
                            <Radio.Button value={JourneyAudienceEvaluationFrequency.STEP_ENTRY}>
                                Journey & Step Entry
                            </Radio.Button>
                            <Radio.Button value={JourneyAudienceEvaluationFrequency.CAMPAIGN_ENTRY}>
                                Journey Entry
                            </Radio.Button>
                        </Radio.Group>
                    </Well>
                </Skeleton>
            </Well>
        )
    }

    protected handleChannelSelectionChange = (channels: DeliveryChannel[]) => {
        const updated = structuredClone(this.context.journey)
        updated.channels = channels

        this.context.setJourney(updated)
    }

    protected handleAudienceTypeChange = (type: string) => {
        const updated = structuredClone(this.context.journey)
        const audience = updated.configuration.audience

        if (type === 'all') {
            audience.segment_ids = [this.context.domainDefaultSegment!.id]
        } else {
            audience.segment_ids = []
        }

        this.context.setFlags({ criteriaEnabled: false })
        this.context.setJourney(updated)
    }

    protected handleEvaluationFreqChange = (ev: RadioChangeEvent) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.audience = {
            ...updated.configuration.audience,
            evaluation_frequency: ev.target.value,
        }

        this.context.setJourney(updated)
    }

    protected handleCriteriaEnabledChange = (criteriaEnabled: boolean) => {
        this.handleCriteriaChange(criteriaEnabled ? {} : undefined)
        this.context.setFlags({ criteriaEnabled })
    }

    protected handleCriteriaChange = (criteria: PQueryDocument | undefined) => {
        const updated = structuredClone(this.context.journey)
        const trigger = extractJourneyTrigger(updated)
        const triggerConfig = trigger?.configuration

        if (trigger) {
            const prevParams = triggerConfig?.params
            trigger.configuration = {
                ...triggerConfig,
                params: stripUndefined({
                    ...prevParams,
                    segments: undefined,
                    criteria,
                }),
            } as ITriggerStepConfiguration
        }

        this.context.setJourney(updated)
    }

    protected handleItemGroupSelectionChange = (itemGroupIds: number[]) => {
        const updated = structuredClone(this.context.journey)

        updated.configuration.item_group_targeting = {
            item_group_ids: itemGroupIds,
        }
        this.context.setJourney(updated)
    }

    protected handleItemGroupsEnabledChange = (itemGroupsEnabled: boolean) => {
        if (!itemGroupsEnabled) {
            const updated = structuredClone(this.context.journey)
            updated.configuration.item_group_targeting = undefined
            this.context.setJourney(updated)
        }
        this.context.setFlags({ itemGroupsEnabled })
    }

    protected handleNewSegmentClick = (type: ComboSegmentSelectionType, name: string | undefined) => {
        this.setState({
            showSegmentDrawer: true,
            drawerId: generateUID(),
            newSegmentType: type,
            newSegmentName: name,
            segmentSearchTerm: undefined,
        })
    }

    protected handleSegmentCreation = async () => {
        const segment = await this.segmentBuilderRef?._submit()
        const type = this.state.newSegmentType

        if (segment?.id) {
            this.closeDrawer()

            const updated = structuredClone(this.context.journey)
            const audience = updated.configuration.audience

            if (type === 'included') {
                const segmentIds = audience.segment_ids ?? []
                segmentIds.push(segment.id)
                audience.segment_ids = segmentIds
            } else {
                const segmentIds = audience.excluded_segment_ids ?? []
                segmentIds.push(segment.id)
                audience.excluded_segment_ids = segmentIds
            }

            this.context.setJourney(updated)

            this.loadSegmentOptions()
        }
    }

    protected closeDrawer = () => {
        this.setState({
            showSegmentDrawer: false,
            drawerId: undefined,
            newSegmentType: undefined,
            newSegmentName: undefined,
        })
    }

    protected async loadItemGroupField() {
        await this.loadItemGroups()

        this.setState({
            itemGroupsReady: true,
        })
    }

    protected async loadItemGroups() {
        const { domain } = this.context
        this.setState({ itemGroupsLoaded: false })

        const { ok, data } = await this.domainSvc.fetchItemGroupsByDomainId(domain.id)

        if (ok) {
            this.setState({
                itemGroupsLoaded: true,
                itemGroups: data,
            })
        }
    }

    protected async loadCriteriaFields() {
        await this.loadLanguageCodes()

        this.setState({
            criteriaFieldsReady: true,
        })
    }

    protected async loadLanguageCodes() {
        this.setState({ languageCodesLoaded: false })

        const { ok, data } = await this.dictionarySvc.fetchLanguageCodes()

        if (ok) {
            this.setState({
                languageCodesLoaded: true,
                languageCodes: data,
            })
        }
    }

    protected loadInitiallySelectedSegments = async (): Promise<SegmentDto[]> => {
        const { journey, domain } = this.context
        const audience = journey.configuration.audience
        const segmentIds = audience?.segment_ids
        const xSegmentIds = audience?.excluded_segment_ids
        const hasSegments = !!segmentIds || !!xSegmentIds

        let loadedSegments: SegmentDto[] = []

        if (hasSegments) {
            // batch each individual segment request
            const initialSegmentIds = [...(segmentIds ?? []), ...(xSegmentIds ?? [])]
            if (initialSegmentIds.length > 0) {
                const res = await this.domainSvc.fetchSegmentsByDomainId(domain.id, {
                    query: {
                        include_deleted: true,
                        id: initialSegmentIds.join(','),
                    },
                    showLoadingScreen: false,
                    cancellationKey: 'aw-resource-loader',
                })

                if (res.ok) {
                    loadedSegments = res.data
                }
            }
        }

        return loadedSegments
    }

    protected extractOption = (segment: SegmentDto) => ({
        value: segment.id,
        label: segment.name,
        title: segment.name,
    })

    protected renderNewSegmentOption = (type: ComboSegmentSelectionType, isEmptyMode: boolean) => {
        return (
            <div
                className={getClassNames(null, 'new-segment', {
                    [`type-${type}`]: true,
                    ['no-data']: isEmptyMode,
                })}
                onClick={() => this.handleNewSegmentClick(type, this.state.segmentSearchTerm)}
            >
                Create New Segment
            </div>
        )
    }

    protected loadSegmentOptions = async () => {
        this.setState({ optionsLoaded: false })

        const nextState: Pick<IState, 'optionsLoaded' | 'options'> = {
            optionsLoaded: true,
            options: this.state.options,
        }

        try {
            nextState.options = await this.props.getSegments()
        } catch (err) {
            console.warn('Unable to fetch segment options.', err)
        }

        this.setState(nextState)
    }
}
